import { Disclosure, Menu, Transition } from "@headlessui/react";
import { ArrowLeft, HambergerMenu, People, SearchNormal } from "iconsax-react";
import { Fragment, useContext, useState, useEffect } from "react";
import { Link, Outlet } from "react-router-dom";
import {
  CurrentNavigationRouteContext,
  NavigationRoutesContext,
} from "./navbar_context";
import Sidebar from "../components/sidebar.jsx";
import { removeUser, getUser } from "../../services/local_service.js";

// logout for user
const handleLogout = (props) => {
  if (props === "logout") {
    removeUser();
    window.location = `/login/developer`;
  }
};

const userNavigation = [
  { name: "Sign out", onclick: "logout", href: "#" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Navbar = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { navigationRoutes } = useContext(NavigationRoutesContext);
  const { currentRoute, setCurrentRoute } = useContext(
    CurrentNavigationRouteContext
  );

  // Get actual user data
  const user = getUser();

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && showSidebar && !event.target.closest('.sidebar-container')) {
        setShowSidebar(false);
      }
    };

    if (showSidebar) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSidebar, isMobile]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Overlay for mobile */}
      {isMobile && showSidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Original Sidebar with Mobile Width Adjustment */}
      <div
        className={`sidebar-container top-0 bottom-0 ${
          isMobile ? 'w-full max-w-sm' : 'w-[21vw]'
        } rounded-md m-1 bg-gray-800 p-2 text-white fixed z-40 ease-in-out duration-300 overflow-auto`}
        style={{
          left: showSidebar ? "0" : "-100%",
          backgroundColor: "#1f2937",
        }}
        ref={(el) => {
          if (el)
            el.style.setProperty("background-color", "#1f2937", "important");
        }}
      >
        {/* Original Close Button */}
        <div className="flex justify-end">
          <button
            className="flex text-4xl text-white items-center cursor-pointer fixed z-50"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <span className="sr-only">Close Sidebar</span>
            <ArrowLeft className="h-8 w-8" aria-hidden="true" />
          </button>
        </div>

        {/* Original Navigation */}
        <h3 className="mt-20 text-4xl font-semibold text-white">
          {navigationRoutes.map((item) => (
            <Sidebar
              key={item.title}
              item={item}
              callback={(title) => {
                setShowSidebar(!showSidebar);
                setCurrentRoute(title);
              }}
              selectedTitle={currentRoute}
            />
          ))}
        </h3>
      </div>
      {/* Sidebar end */}

      <Disclosure as="nav" className="bg-white shadow-sm">
        {({ open }) => (
          <>
            <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex items-center"> {/* Added 'flex' class here */}
                  <button
                    type="button"
                    onClick={() => setShowSidebar(!showSidebar)}
                    className="bg-slate-900 p-1 rounded-md text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-950 inline-flex items-center justify-center"
                  >
                    <span className="sr-only">Sidebar</span>
                    <HambergerMenu className="h-8 w-8" aria-hidden="true" />
                  </button>
                </div>
              </div>
                <div className="hidden sm:ml-6 sm:flex sm:items-center">
                  {/* Profile dropdown */}
                  <Menu as="div" className="ml-3 relative">
                    <div>
                      <Menu.Button className="bg-white flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <span className="sr-only">Open user menu</span>
                        <img
                          className="h-8 w-8 rounded-full"
                          src={user?.imageUrl || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"}
                          alt={user?.name || "User"}
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {userNavigation.map((item) => (
                          <Menu.Item key={item.name}>
                            {({ active }) => (
                              <Link
                                to={item.href}
                                onClick={() => handleLogout(item.onclick)}
                                className={classNames(
                                  active ? "bg-gray-100" : "",
                                  "block px-4 py-2 text-sm text-gray-700"
                                )}
                              >
                                {item.name}
                              </Link>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
                <div className="-mr-2 flex items-center sm:hidden">
                  {/* Mobile profile dropdown - same as desktop */}
                  <Menu as="div" className="ml-3 relative">
                    <div>
                      <Menu.Button className="bg-white flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <span className="sr-only">Open user menu</span>
                        <img
                          className="h-8 w-8 rounded-full"
                          src={user?.imageUrl || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"}
                          alt={user?.name || "User"}
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {userNavigation.map((item) => (
                          <Menu.Item key={item.name}>
                            {({ active }) => (
                              <Link
                                to={item.href}
                                onClick={() => handleLogout(item.onclick)}
                                className={classNames(
                                  active ? "bg-gray-100" : "",
                                  "block px-4 py-2 text-sm text-gray-700"
                                )}
                              >
                                {item.name}
                              </Link>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>
          </>
        )}
      </Disclosure>

      <Outlet />
    </div>
  );
};

export default Navbar;